/**
 * One place to build and open map links so we can (a) set resume markers for
 * foreground recovery and (b) keep the URL/flags consistent.
 *
 * Session keys (same-tab session) — read by `useAppResumeRecovery` in App.
 */
import { recordHiddenWallClock, resetMapResumeLifecycle } from './mapResumeLifecycle'
import { resumeBreadcrumb } from './resumeTelemetry'

export const MAP_DEPART_KEY = 'puttzin_map_depart_ms'
export const MAP_LAST_HIDDEN_KEY = 'puttzin_last_hidden_ms'

/**
 * Universal Maps URL (Google resolves the address server-side).
 * After navigation, all map tiles / routing / traffic load inside Google’s app
 * or site — not Puttzin; slow LTE + cold Maps dominates perceived load time.
 *
 * @param {string} address
 * @returns {string}
 */
export function buildGoogleMapsUrl(address) {
  if (typeof address !== 'string' || !address.trim()) {
    return ''
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`
}

/**
 * Mark that the user is opening an external map so we can reload the SPA on
 * return from long background (fixes iOS/WebKit white screen after maps).
 * Call immediately before opening the map URL (same tab or new window).
 * Clears last-hidden so an older tab-background event cannot trigger a spurious
 * reload when the user returns to the app.
 */
export function markMapDirectionsOpened() {
  resetMapResumeLifecycle()
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(MAP_DEPART_KEY, String(Date.now()))
      sessionStorage.removeItem(MAP_LAST_HIDDEN_KEY)
    }
  } catch {
    // ignore
  }
  resumeBreadcrumb('map_depart_marked')

  // If WebKit already transitioned to hidden in the same turn as open, capture it
  // so `maybeReloadAfterMapReturn` still sees a post-depart hidden timestamp.
  queueMicrotask(() => {
    requestAnimationFrame(() => {
      try {
        if (typeof document === 'undefined' || typeof sessionStorage === 'undefined') return
        if (document.visibilityState !== 'hidden') return
        const t = Date.now()
        sessionStorage.setItem(MAP_LAST_HIDDEN_KEY, String(t))
        recordHiddenWallClock(t)
        resumeBreadcrumb('map_depart_hidden_flush', { t })
      } catch {
        // ignore
      }
    })
  })
}

/**
 * `window.open(..., '_blank')` on phones often leaves a tab stuck on `about:blank`
 * when the OS hands the URL to the native Maps app — users switch tabs and land on
 * that blank tab. Prefer same-tab navigation on coarse pointers / touch devices.
 * @returns {boolean}
 */
function prefersExternalMapsSameTab() {
  if (typeof window === 'undefined') return false
  try {
    if (window.matchMedia('(pointer: coarse)').matches) return true
  } catch {
    // ignore
  }
  return typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0
}

/**
 * @param {string} address
 * @returns {boolean} false if no URL was opened
 */
export function openDirectionsInNewWindow(address) {
  const url = buildGoogleMapsUrl(address)
  if (!url) return false
  markMapDirectionsOpened()
  if (prefersExternalMapsSameTab()) {
    resumeBreadcrumb('maps_open_same_tab')
    window.location.assign(url)
  } else {
    resumeBreadcrumb('maps_open_new_tab')
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  return true
}

/**
 * For `<a>` elements: plain left-click opens maps (same tab on touch/coarse
 * devices, new tab on desktop) and marks for resume recovery. Modified-clicks
 * and are not marked here — use a normal click if you need the blank-screen reload.
 *
 * @param {import('react').MouseEvent} e
 * @param {string} address
 */
export function onDirectionsLinkClick(e, address) {
  if (e.defaultPrevented) return
  if (e.button !== 0) return
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  e.preventDefault()
  openDirectionsInNewWindow(address)
}
